import { ButtonLink } from './ui/Button';
import { Container } from './ui/Container';
import { Icon } from './ui/Icon';
import { useShared } from './layout/Layout';
import { t } from '../i18n/t';

export interface CtaBannerProps {
  title?: string;
  description?: string;
  insuranceType?: string;
}

/** Closing call to action reused at the bottom of every content page. */
export function CtaBanner({
  title,
  description,
  insuranceType,
}: CtaBannerProps) {
  const { brand } = useShared();
  const applicationPath = insuranceType ? `/pieteikums?veids=${insuranceType}` : '/pieteikums';
  const heading = title ?? t('ui.cta.title');
  const body = description ?? t('ui.cta.description');

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-700 py-16 sm:py-20"
      aria-labelledby="cta-heading"
    >
      <div
        className="pointer-events-none absolute -right-16 top-0 h-64 w-64 rounded-full bg-accent-400/20 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-secondary-400/25 blur-3xl"
        aria-hidden="true"
      />

      <Container>
        <div className="relative max-w-2xl">
          <h2
            id="cta-heading"
            className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl"
          >
            {heading}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-white/85">{body}</p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonLink to={applicationPath} variant="accent" size="lg">
              {t('ui.cta.apply')}
              <Icon name="arrow-right" className="h-5 w-5" strokeWidth={2} />
            </ButtonLink>

            <a
              href={brand.phoneHref}
              className="inline-flex min-h-14 items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-base font-semibold text-white ring-1 ring-inset ring-white/35 transition hover:bg-white/10"
            >
              <Icon name="phone" className="h-5 w-5" />
              {brand.phone}
            </a>
          </div>

          <p className="mt-5 text-sm text-white/70">{brand.workingHours}</p>
        </div>
      </Container>
    </section>
  );
}
